import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { RecipientMap, ContractListItem } from '@getaccept/editor-lib';
import type { Participant } from '@getaccept/dsr-shared';
import { EditorBlockService } from '../api/services/editor-block.service';
import type { ContractFilter } from '../types';

export class ContractHelper {
  static async loadContracts(filterPayload: ContractFilter) {
    try {
      const contracts = await EditorBlockService.loadDocuments(filterPayload);
      return ContractHelper.filterSignableContracts(contracts);
    } catch (error) {
      return null;
    }
  }

  static async loadContract(contractId: string) {
    try {
      return await EditorBlockService.loadDocument(contractId);
    } catch (error) {
      return null;
    }
  }

  static filterSignableContracts(contracts: ContractListItem[]) {
    if (!contracts) return [];

    return contracts
      .filter(contract => contract.isSigning === 1)
      .filter(contract =>
        contract.recipients.every(recipient => !recipient.verifyQnaOpen && !recipient.verifySmsOpen)
      );
  }

  static mapRecipientsToParticipants(
    recipients: Recipient[],
    participants: Recipient[]
  ): RecipientMap[] {
    if (!recipients?.length) return [];
    return recipients.map(recipient => {
      const participant = participants.find(
        p => p.contactId === recipient.contactId
      ) as Participant;

      return {
        participantId: participant ? participant.id : '',
        contractRecipientId: recipient.id,
      };
    });
  }

  static updateRecipientMaps(
    recipientMap: RecipientMap[],
    recipients: Recipient[],
    participant: Participant,
    addParticipant: boolean
  ): RecipientMap[] {
    if (!recipients?.length || !participant) return [];
    return recipientMap.map(map => {
      const recipient = recipients.find(r => r.id === map.contractRecipientId);
      const shouldAddParticipant = addParticipant && !map.participantId;
      const shouldRemoveParticipant = !addParticipant && !!map.participantId;

      if (
        !recipient ||
        recipient.contactId !== participant.contactId ||
        (!shouldAddParticipant && !shouldRemoveParticipant)
      ) {
        return map;
      }

      return shouldRemoveParticipant
        ? {
            participantId: '',
            contractRecipientId: map.contractRecipientId,
          }
        : {
            participantId: participant.id,
            contractRecipientId: map.contractRecipientId,
          };
    });
  }

  static checkRecipientMapsEquality(a: RecipientMap[], b: RecipientMap[]) {
    if (!a?.length && !b?.length) return true;
    if (!a?.length || !b?.length) return false;
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, index) => {
      const bItem = b[index];
      return (
        item.contractRecipientId === bItem.contractRecipientId &&
        item.participantId === bItem.participantId
      );
    });
  }
}
